/**
 * ISO 3166-1 alpha-2 country codes enum
 * Used for multi-select country selection components
 * Focused on Europe and surrounding regions used in Timocom
 */
export enum CountryCode {
  AD = 'AD', // Andorra
  AL = 'AL', // Albania
  AT = 'AT', // Austria
  BA = 'BA', // Bosnia and Herzegovina
  BE = 'BE', // Belgium
  BG = 'BG', // Bulgaria
  BY = 'BY', // Belarus
  CH = 'CH', // Switzerland
  CY = 'CY', // Cyprus
  CZ = 'CZ', // Czech Republic
  DE = 'DE', // Germany
  DK = 'DK', // Denmark
  EE = 'EE', // Estonia
  ES = 'ES', // Spain
  FI = 'FI', // Finland
  FR = 'FR', // France
  GB = 'GB', // United Kingdom
  GE = 'GE', // Georgia
  GR = 'GR', // Greece
  HR = 'HR', // Croatia
  HU = 'HU', // Hungary
  IE = 'IE', // Ireland
  IT = 'IT', // Italy
  LI = 'LI', // Liechtenstein
  LT = 'LT', // Lithuania
  LU = 'LU', // Luxembourg
  LV = 'LV', // Latvia
  MC = 'MC', // Monaco
  MD = 'MD', // Moldova
  ME = 'ME', // Montenegro
  NL = 'NL', // Netherlands
  NO = 'NO', // Norway
  PL = 'PL', // Poland
  PT = 'PT', // Portugal
  RO = 'RO', // Romania
  RS = 'RS', // Serbia
  RU = 'RU', // Russia
  SE = 'SE', // Sweden
  SI = 'SI', // Slovenia
  SK = 'SK', // Slovakia
  SM = 'SM', // San Marino
  TR = 'TR', // Turkey
  TN = 'TN', // Tunisia
  UA = 'UA', // Ukraine
  VA = 'VA', // Vatican City
}

/**
 * Helper object to get country names from country codes
 * Useful for displaying human-readable country names in UI components
 */
export const COUNTRY_NAMES: Record<CountryCode, string> = {
  [CountryCode.AD]: 'Andorra',
  [CountryCode.AL]: 'Albania',
  [CountryCode.AT]: 'Austria',
  [CountryCode.BA]: 'Bosnia and Herzegovina',
  [CountryCode.BE]: 'Belgium',
  [CountryCode.BG]: 'Bulgaria',
  [CountryCode.BY]: 'Belarus',
  [CountryCode.CH]: 'Switzerland',
  [CountryCode.CY]: 'Cyprus',
  [CountryCode.CZ]: 'Czech Republic',
  [CountryCode.DE]: 'Germany',
  [CountryCode.DK]: 'Denmark',
  [CountryCode.EE]: 'Estonia',
  [CountryCode.ES]: 'Spain',
  [CountryCode.FI]: 'Finland',
  [CountryCode.FR]: 'France',
  [CountryCode.GB]: 'United Kingdom',
  [CountryCode.GE]: 'Georgia',
  [CountryCode.GR]: 'Greece',
  [CountryCode.HR]: 'Croatia',
  [CountryCode.HU]: 'Hungary',
  [CountryCode.IE]: 'Ireland',
  [CountryCode.IT]: 'Italy',
  [CountryCode.LI]: 'Liechtenstein',
  [CountryCode.LT]: 'Lithuania',
  [CountryCode.LU]: 'Luxembourg',
  [CountryCode.LV]: 'Latvia',
  [CountryCode.MC]: 'Monaco',
  [CountryCode.MD]: 'Moldova',
  [CountryCode.ME]: 'Montenegro',
  [CountryCode.NL]: 'Netherlands',
  [CountryCode.NO]: 'Norway',
  [CountryCode.PL]: 'Poland',
  [CountryCode.PT]: 'Portugal',
  [CountryCode.RO]: 'Romania',
  [CountryCode.RS]: 'Serbia',
  [CountryCode.RU]: 'Russia',
  [CountryCode.SE]: 'Sweden',
  [CountryCode.SI]: 'Slovenia',
  [CountryCode.SK]: 'Slovakia',
  [CountryCode.SM]: 'San Marino',
  [CountryCode.TR]: 'Turkey',
  [CountryCode.TN]: 'Tunisia',
  [CountryCode.UA]: 'Ukraine',
  [CountryCode.VA]: 'Vatican City',
};

/**
 * Helper function to get country code from country name
 * Useful for converting user input like "Spain" to "ES"
 * @param countryName - The country name (case-insensitive)
 * @returns The country code if found, null otherwise
 */
export function getCountryCodeFromName(countryName: string): CountryCode | null {
  const normalizedName = countryName.toLowerCase().trim();
  
  for (const [code, name] of Object.entries(COUNTRY_NAMES)) {
    if (name.toLowerCase() === normalizedName) {
      return code as CountryCode;
    }
  }
  
  return null;
}

/**
 * Helper function to get country code from partial country name
 * Useful for autocomplete and fuzzy matching
 * @param partialName - Partial country name (case-insensitive)
 * @returns Array of matching country codes
 */
export function getCountryCodesFromPartialName(partialName: string): CountryCode[] {
  const normalizedPartial = partialName.toLowerCase().trim();
  
  return Object.entries(COUNTRY_NAMES)
    .filter(([_, name]) => name.toLowerCase().includes(normalizedPartial))
    .map(([code, _]) => code as CountryCode);
}
